import React, { useState, useEffect, useRef } from 'react';
import { interpolate, spring } from 'remotion';

// Configuration
const FPS = 30;
const DURATION_IN_FRAMES = 1200; // 40 seconds at 30fps
const COMPOSITION_WIDTH = 960;
const COMPOSITION_HEIGHT = 1080;

// Custom spring function that works without Remotion context
const customSpring = ({ frame, fps, config, durationInFrames }) => {
    return spring({
        frame,
        fps,
        config,
        durationInFrames,
    });
};

// World Map Image with elegant fade
const WorldMap = ({ frame }) => {
    // Fade in the map smoothly after intro
    const opacity = interpolate(
        frame,
        [100, 160],
        [0, 0.6],
        { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
    );

    // Subtle zoom
    const scale = interpolate(
        frame,
        [100, 1200],
        [1, 1.05],
        { extrapolateRight: 'clamp' }
    );

    return (
        <img
            src="/world-map.png"
            alt="World Map"
            style={{
                position: 'absolute',
                width: '120%',
                height: 'auto',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) scale(${scale})`,
                opacity,
                objectFit: 'contain',
            }}
        />
    );
};

// Elegant Country Word with Glass Glow Effect
const CountryWord = ({ text, index, startFrame, frame, containerWidth, containerHeight }) => {
    // Seeded random position
    const seed1 = ((index * 137.508) % 100) / 100;
    const seed2 = (((index + 1) * 137.508) % 100) / 100;
    const seed3 = (((index + 2) * 137.508) % 100) / 100;

    // Scale positions based on container size
    const width = containerWidth || COMPOSITION_WIDTH;
    const height = containerHeight || COMPOSITION_HEIGHT;

    const x = interpolate(seed1, [0, 1], [100, width - 200]); // Avoid edges

    // Restrict Y to center area (around map) to avoid header overlap
    const minY = height * 0.35; // Start 35% down
    const maxY = height * 0.75; // End 75% down
    const y = interpolate(seed2, [0, 1], [minY, maxY]);

    const activeFrame = frame - startFrame;

    // Total lifecycle: 200 frames
    if (activeFrame < 0 || activeFrame > 200) return null;

    // Elegant fade in with spring (slow, soft)
    const springProgress = customSpring({
        frame: activeFrame,
        fps: FPS,
        config: {
            damping: 25,
            stiffness: 80,
            mass: 1.2,
        },
        durationInFrames: 60,
    });

    // Opacity: soft fade in, hold, soft fade out
    const opacity = interpolate(
        activeFrame,
        [0, 45, 150, 200],
        [0, 0.85, 0.85, 0],
        { extrapolateRight: 'clamp' }
    );

    // Scale: gentle pulse during visible phase
    const pulseScale = interpolate(
        Math.sin((activeFrame - 45) / 30),
        [-1, 1],
        [0.98, 1.02]
    );
    const scale = activeFrame < 45 ? springProgress : pulseScale;

    // Blur: fog-like fade out
    const blur = interpolate(
        activeFrame,
        [150, 200],
        [0, 12],
        { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
    );

    // Font size variation
    const fontSize = interpolate(seed3, [0, 1], [18, 32]);

    return (
        <div
            style={{
                position: 'absolute',
                left: x,
                top: y,
                opacity,
                transform: `scale(${scale})`,
                filter: `blur(${blur}px)`,
                fontSize,
                fontWeight: 500,
                color: '#E2E8F0',
                textShadow: `
                    0 0 8px rgba(59, 130, 246, 0.6),
                    0 0 20px rgba(59, 130, 246, 0.4),
                    0 0 40px rgba(59, 130, 246, 0.2),
                    0 2px 4px rgba(0, 0, 0, 0.3)
                `,
                fontFamily: 'Inter, sans-serif',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                zIndex: 10 + index,
            }}
        >
            {text}
        </div>
    );
};

// Single text line with Fade + Slide Up + Blur effect
const FadeSlideText = ({ children, frame, startFrame, duration = 30, style }) => {
    const activeFrame = frame - startFrame;

    // Opacity: 0 → 1
    const opacity = interpolate(
        activeFrame,
        [0, duration],
        [0, 1],
        { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
    );

    // Slide Up: 40px → 0px
    const translateY = interpolate(
        activeFrame,
        [0, duration],
        [40, 0],
        { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
    );

    // Blur: 8px → 0px (sharp)
    const blur = interpolate(
        activeFrame,
        [0, duration * 0.7],
        [8, 0],
        { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
    );

    return (
        <div
            style={{
                opacity,
                transform: `translateY(${translateY}px)`,
                filter: `blur(${blur}px)`,
                ...style,
            }}
        >
            {children}
        </div>
    );
};

// Header: Center → Top-Center transition with Fade + Slide Up effect
const AnimatedHeader = ({ frame, containerWidth }) => {
    const width = containerWidth || COMPOSITION_WIDTH;

    // Stagger timing (15 frames = 0.5s at 30fps apart)
    const LINE1_START = 0;      // AGENT DIRECTORY
    const LINE2_START = 15;     // Internal Agent Information System
    const LINE3_START = 30;     // Divider line
    const LINE4_START = 45;     // QUANTERM LOGISTICS VIETNAM
    const ANIMATION_DURATION = 30; // 1 second fade in

    // Position: Center (0-120) → Top-Center (120-170)
    const transitionStart = 120;
    const transitionEnd = 170;

    // Keep X position centered always
    const xPos = width / 2;

    // Y Position: Center (300) → Top (60)
    const yPos = interpolate(
        frame,
        [transitionStart, transitionEnd],
        [300, 60],
        { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
    );

    // Scale: 1.0 → 0.6
    const scale = interpolate(
        frame,
        [transitionStart, transitionEnd],
        [1, 0.6],
        { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
    );

    const isCentered = frame < transitionStart;

    return (
        <div
            style={{
                position: 'absolute',
                left: xPos,
                top: yPos,
                transform: `translate(-50%, 0) scale(${scale})`, // Always center horizontally
                zIndex: 100,
                textAlign: 'center', // Always center text
                width: '100%', // Ensure width for centering
            }}
        >
            {/* Line 1: AGENT DIRECTORY */}
            <FadeSlideText
                frame={frame}
                startFrame={LINE1_START}
                duration={ANIMATION_DURATION}
            >
                <h1
                    style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: 52,
                        fontWeight: 700,
                        margin: 0,
                        color: '#F8FAFC',
                        textShadow: '0 0 30px rgba(59, 130, 246, 0.4)',
                    }}
                >
                    AGENT DIRECTORY
                </h1>
            </FadeSlideText>

            {/* Line 2: Internal Agent Information System */}
            <FadeSlideText
                frame={frame}
                startFrame={LINE2_START}
                duration={ANIMATION_DURATION}
                style={{ marginTop: 8 }}
            >
                <p
                    style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 18,
                        color: '#94A3B8',
                        margin: 0,
                    }}
                >
                    Internal Agent Information System
                </p>
            </FadeSlideText>

            {/* Line 3: Divider */}
            <FadeSlideText
                frame={frame}
                startFrame={LINE3_START}
                duration={ANIMATION_DURATION}
                style={{ marginTop: 16 }}
            >
                <div
                    style={{
                        width: 200,
                        height: 1,
                        background: 'linear-gradient(90deg, transparent, #3B82F6, transparent)',
                        margin: '0 auto',
                    }}
                />
            </FadeSlideText>

            {/* Line 4: QUANTERM LOGISTICS VIETNAM */}
            <FadeSlideText
                frame={frame}
                startFrame={LINE4_START}
                duration={ANIMATION_DURATION}
                style={{ marginTop: 16 }}
            >
                <p
                    style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: 20,
                        fontWeight: 600,
                        color: '#60A5FA',
                        margin: 0,
                        letterSpacing: '1px',
                    }}
                >
                    QUANTERM LOGISTICS VIETNAM
                </p>
            </FadeSlideText>
        </div>
    );
};

// Main Combined Animation - Now a regular React component
export const CombinedAnimation = () => {
    const [frame, setFrame] = useState(0);
    const containerRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ width: COMPOSITION_WIDTH, height: COMPOSITION_HEIGHT });

    const COUNTRIES = [
        "Vietnam", "Singapore", "United States", "China",
        "Germany", "Japan", "South Korea", "India",
        "United Kingdom", "Australia", "Malaysia", "France",
        "Canada", "UAE", "Thailand", "Brazil", "Netherlands",
        "Indonesia", "Philippines", "Taiwan", "Hong Kong", "Mexico"
    ];

    // Stagger country appearances: start at frame 180, every 50 frames
    const countryStartFrames = COUNTRIES.map((_, i) => 180 + i * 50);

    // Frame loop - self-managed animation
    useEffect(() => {
        const interval = setInterval(() => {
            setFrame(f => (f + 1) % DURATION_IN_FRAMES);
        }, 1000 / FPS);

        return () => clearInterval(interval);
    }, []);

    // Track container size for responsive positioning
    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                setContainerSize({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight,
                });
            }
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    return (
        <div
            ref={containerRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(145deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)',
                overflow: 'hidden',
            }}
        >
            {/* World Map */}
            <WorldMap frame={frame} />

            {/* Header */}
            <AnimatedHeader frame={frame} containerWidth={containerSize.width} />

            {/* Country Words */}
            {COUNTRIES.map((country, index) => (
                <CountryWord
                    key={country}
                    text={country}
                    index={index}
                    startFrame={countryStartFrames[index]}
                    frame={frame}
                    containerWidth={containerSize.width}
                    containerHeight={containerSize.height}
                />
            ))}

            {/* Soft vignette */}
            <div style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(ellipse at center, transparent 30%, rgba(15, 23, 42, 0.6) 100%)',
                pointerEvents: 'none',
            }} />
        </div>
    );
};

export default CombinedAnimation;
